import { type FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { _isNotBlank } from '@/littledash';
import Lookup from '@/components/UI/Lookup';
import type { SelectFieldProps, SelectOption } from '../VerticalStepForm.model';

/**
 * Async paginated select field. Wraps the Lookup component with RHF Controller.
 * The form value holds the full selected option ({ label, value }) so the
 * label survives step collapse/reopen; VerticalStepForm flattens select fields
 * to their primitive value before invoking onSubmit.
 */
const SelectField: FC<SelectFieldProps> = ({ config, onValueChange }) => {
  const { name, label, showLabel, placeholder, loadOptions, rules } = config;
  const { control } = useFormContext();

  return (
    // Constrain select width; the upload field fills the step, so the cap is per-field.
    <div style={{ maxWidth: 400 }}>
      {showLabel && (
        <label htmlFor={name} className="db mb1 f6 near-black basier-med">
          {label}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        rules={{
          ...rules,
          validate: rules?.required
            ? (value) =>
                _isNotBlank(value?.value) ? true : typeof rules.required === 'string' ? rules.required : 'Required'
            : undefined,
        }}
        render={({ field: { onChange, value } }) => (
          <Lookup
            name={name}
            inputId={name}
            loadOptions={loadOptions}
            handleSelect={(data: SelectOption | null) => {
              onChange(data ?? null);
              onValueChange?.();
            }}
            value={value ?? null}
            placeholder={placeholder ?? 'Search...'}
            cacheOptions={false}
          />
        )}
      />
    </div>
  );
};

export default SelectField;
